import './index.css';
import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Modal, Row, Col, Typography, Card, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { LockOutlined, KeyOutlined, MailOutlined, ArrowLeftOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import API_ROUTES from '../../api';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;

export default function ResetPwd() {
    const navigate = useNavigate();
    const [isDisabled, setIsDisabled] = useState(false);
    const [countdown, setCountdown] = useState(
        localStorage.getItem('countdown') ? JSON.parse(localStorage.getItem('countdown')) : 0
    );

    const onFinish = (values) => {
        axios.post(API_ROUTES.api_change_pwd, {
            password: values.password,
            secretKey: values.secretKey
        }, {
            headers: {
                Authorization: localStorage.getItem('userInfo')
            }
        }).then(function (response) {
            if (response.data.code === 500) {
                Modal.error({
                    title: 'Update Failed',
                    content: response.data.msg
                });
            } else {
                Modal.success({
                    title: 'Success',
                    content: "Password changed successfully! Please login again."
                });
                localStorage.clear();
                navigate('/login');
            }
        }).catch(function (error) {
            Modal.error({
                title: 'Error',
                content: error.response?.data?.msgDes || "An error occurred while changing password."
            });
            if (error.response?.status === 401) {
                navigate('/login');
            }
        });
    };

    const handleButtonClick = () => {
        axios.post(API_ROUTES.api_auth, {
            email: localStorage.getItem('email'),
        }).then(function (response) {
            Modal.success({
                title: 'Verification Sent',
                content: 'A secret key has been sent to your registered email.'
            });
        }).catch(function (error) {
            Modal.error({
                title: 'Failed',
                content: 'Failed to send secret key. Please try again later.'
            });
            setIsDisabled(false);
            localStorage.removeItem('countdown');
        });

        setCountdown(60); // Standard 60s countdown
        setIsDisabled(true);
    };

    useEffect(() => {
        let timer;
        if (countdown > 0) {
            timer = setInterval(() => {
                setCountdown((prevCountdown) => {
                    const nextValue = prevCountdown - 1;
                    localStorage.setItem('countdown', JSON.stringify(nextValue));
                    return nextValue;
                });
            }, 1000);
        } else {
            setIsDisabled(false);
            localStorage.removeItem('countdown');
        }

        return () => clearInterval(timer);
    }, [countdown]);

    return (
        <div className="shared-auth-page" style={{ backgroundColor: '#f0f5ff', minHeight: '100vh', paddingBottom: '80px' }}>
            {/* Header Section */}
            <div className="shared-auth-hero-banner" style={{
                height: '240px',
                padding: '60px 20px',
                textAlign: 'center',
                color: 'white',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}>
                <Button 
                    type="text" 
                    icon={<ArrowLeftOutlined />} 
                    style={{ color: 'white', position: 'absolute', left: '20px', top: '20px' }}
                    onClick={() => navigate(-1)}
                >
                    Back
                </Button>
                <Title level={1} style={{ color: 'white', margin: 0, fontWeight: 800 }}>Account Security</Title>
                <Paragraph style={{ color: 'rgba(255,255,255,0.8)', marginTop: '8px', fontSize: '16px' }}>
                    Secure your account by updating your password regularly.
                </Paragraph>
            </div>

            {/* Form Card */}
            <div className="shared-auth-card-wrap">
                <Card 
                    style={{ 
                        width: '100%', 
                        maxWidth: '550px', 
                        borderRadius: '24px', 
                        boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                        border: 'none'
                    }}
                    bodyStyle={{ padding: '40px' }}
                >
                    <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                        <div style={{ 
                            width: '64px', 
                            height: '64px', 
                            backgroundColor: '#e6f7ff', 
                            borderRadius: '50%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            margin: '0 auto 16px',
                            color: '#055878',
                            fontSize: '28px'
                        }}>
                            <LockOutlined />
                        </div>
                        <Title level={3} style={{ margin: 0, color: '#055878' }}>Change Password</Title>
                        <Text type="secondary">Verification key will be sent to your email</Text>
                    </div>

                    <Form
                        layout="vertical"
                        name="change_password"
                        onFinish={onFinish}
                        requiredMark={false}
                    >
                        <Row gutter={12}>
                            <Col xs={24} sm={16}>
                                <Form.Item
                                    label={<Text strong>Secret Key</Text>}
                                    name="secretKey"
                                    rules={[{ required: true, message: 'Please input the secret key!' }]}
                                >
                                    <Input 
                                        prefix={<KeyOutlined style={{ color: 'rgba(0,0,0,.25)' }} />} 
                                        placeholder="Enter key from email"
                                        size="large"
                                        style={{ borderRadius: '10px' }}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} sm={8}>
                                <Form.Item label="&nbsp;">
                                    <Button
                                        onClick={handleButtonClick}
                                        disabled={isDisabled}
                                        type="primary"
                                        block
                                        size="large"
                                        style={{ 
                                            borderRadius: '10px',
                                            height: '40px',
                                            background: isDisabled ? undefined : '#055878',
                                            fontSize: '13px',
                                            padding: '0 8px'
                                        }}
                                    >
                                        {isDisabled ? `${countdown}s` : 'Get Key'}
                                    </Button>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            label={<Text strong>New Password</Text>}
                            name="password"
                            rules={[{ required: true, message: 'Please input your new password!' }]}
                        >
                            <Input.Password 
                                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                                placeholder="Min. 8 characters"
                                size="large"
                                style={{ borderRadius: '10px' }}
                            />
                        </Form.Item>

                        <Form.Item
                            label={<Text strong>Confirm New Password</Text>}
                            name="confirmPassword"
                            dependencies={['password']}
                            rules={[
                                { required: true, message: 'Please confirm your password!' },
                                ({ getFieldValue }) => ({
                                    validator(_, value) {
                                        if (!value || getFieldValue('password') === value) {
                                            return Promise.resolve();
                                        }
                                        return Promise.reject('The two passwords do not match!');
                                    }
                                })
                            ]}
                        >
                            <Input.Password 
                                prefix={<SafetyCertificateOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                                placeholder="Re-enter password"
                                size="large"
                                style={{ borderRadius: '10px' }}
                            />
                        </Form.Item>

                        <Form.Item style={{ marginTop: '40px', marginBottom: 0 }}>
                            <Button
                                type="primary"
                                htmlType="submit"
                                block
                                size="large"
                                style={{
                                    height: '56px',
                                    borderRadius: '28px',
                                    background: 'linear-gradient(135deg, #055878 0%, #13769a 100%)',
                                    border: 'none',
                                    fontSize: '18px',
                                    fontWeight: '700',
                                    boxShadow: '0 8px 20px rgba(5, 88, 120, 0.25)'
                                }}
                            >
                                Update Password
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
            
            <div style={{ textAlign: 'center', marginTop: '30px' }}>
                <Text type="secondary">
                    Having trouble? <a href="mailto:support@example.com" style={{ color: '#055878' }}>Contact support</a>
                </Text>
            </div>
        </div>
    );
}
